import { Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import type { ReactNode } from 'react';
import Iconify from 'src/components/iconify';

type CCNCoinIconProps = {
  size?: number | string;
  color?: string;
  ringColor?: string;
  blueColor?: string;
  redColor?: string;
};

export function CCNCoinIcon({ size, color, ringColor, blueColor, redColor }: CCNCoinIconProps) {
  const width = size ?? 24;
  const height = size ?? 26;
  const RingColor = color ?? ringColor ?? '#251E1E';
  const BlueColor = color ?? blueColor ?? '#283C75';
  const RedColor = color ?? redColor ?? '#CE0606';

  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: 'block', flexShrink: 0 }}
      aria-hidden
      focusable="false"
    >
      <path
        d="M12 22.5C9.21523 22.5 6.54451 21.3938 4.57538 19.4246C2.60625 17.4555 1.5 14.7848 1.5 12C1.5 9.21523 2.60625 6.54451 4.57538 4.57538C6.54451 2.60625 9.21523 1.5 12 1.5C14.7848 1.5 17.4555 2.60625 19.4246 4.57538C21.3938 6.54451 22.5 9.21523 22.5 12C22.5 14.7848 21.3938 17.4555 19.4246 19.4246C17.4555 21.3938 14.7848 22.5 12 22.5ZM12 24C15.1826 24 18.2348 22.7357 20.4853 20.4853C22.7357 18.2348 24 15.1826 24 12C24 8.8174 22.7357 5.76516 20.4853 3.51472C18.2348 1.26428 15.1826 0 12 0C8.8174 0 5.76516 1.26428 3.51472 3.51472C1.26428 5.76516 0 8.8174 0 12C0 15.1826 1.26428 18.2348 3.51472 20.4853C5.76516 22.7357 8.8174 24 12 24Z"
        fill={RingColor}
      />
      <path
        d="M12 20.25C9.81196 20.25 7.71354 19.3808 6.16637 17.8336C4.61919 16.2865 3.75 14.188 3.75 12C3.75 9.81196 4.61919 7.71354 6.16637 6.16637C7.71354 4.61919 9.81196 3.75 12 3.75C14.188 3.75 16.2865 4.61919 17.8336 6.16637C19.3808 7.71354 20.25 9.81196 20.25 12C20.25 14.188 19.3808 16.2865 17.8336 17.8336C16.2865 19.3808 14.188 20.25 12 20.25ZM12 21C13.1819 21 14.3522 20.7672 15.4442 20.3149C16.5361 19.8626 17.5282 19.1997 18.364 18.364C19.1997 17.5282 19.8626 16.5361 20.3149 15.4442C20.7672 14.3522 21 13.1819 21 12C21 10.8181 20.7672 9.64778 20.3149 8.55585C19.8626 7.46392 19.1997 6.47177 18.364 5.63604C17.5282 4.80031 16.5361 4.13738 15.4442 3.68508C14.3522 3.23279 13.1819 3 12 3C9.61305 3 7.32387 3.94821 5.63604 5.63604C3.94821 7.32387 3 9.61305 3 12C3 14.3869 3.94821 16.6761 5.63604 18.364C7.32387 20.0518 9.61305 21 12 21Z"
        fill={RingColor}
      />
      <path
        d="M8.25146 8.25146L11.9832 8.2516L15.7485 11.9998L12.0168 11.9997L8.25146 8.25146Z"
        fill={BlueColor}
      />
      <path
        d="M8.25146 15.7485H11.9832L15.7485 11.9998L12.0168 11.9997L8.25146 15.7485Z"
        fill={RedColor}
      />
    </svg>
  );
}

type ExhibitionOriginChipProps = {
  value: unknown;
  image?: string | null;
};

type OriginConfig = {
  icon?: ReactNode;
  label?: string;
  image?: string;
  sx: SxProps<Theme>;
};

const getOriginConfig = (value: unknown): OriginConfig | null => {
  const normalizedValue = String(value || '')
    .trim()
    .toLowerCase();

  if (normalizedValue === 'viva') {
    return {
      icon: null,
      image: '/logo/logo_viva.webp',
      sx: {
        position: 'relative',
        overflow: 'hidden',
        color: '#000000',
        background: 'linear-gradient(120deg, #B5DCF06f 0%, #F8F9FC 48%, #FCE4E5 100%)',
        borderColor: '#C9D3E5',
        boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.9)',
        textShadow: '0 1px 1px rgba(0, 0, 0, 0.25)',
      },
    };
  }

  if (normalizedValue === 'whatsapp') {
    return {
      label: 'Whatsapp',
      icon: <Iconify icon="mdi:whatsapp" sx={{ width: 16, height: 16 }} />,
      sx: {
        color: '#126B3A',
        background: 'linear-gradient(120deg, #E9FAF0 0%, #D2F4DF 52%, #B9EACD 100%)',
        borderColor: '#8BD5A7',
        boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.9)',
      },
    };
  }

  if (normalizedValue === 'parking' || normalizedValue === 'estacionamento') {
    return {
      label: 'Estacionamento',
      icon: <Iconify icon="lucide:circle-parking" sx={{ width: 16, height: 16 }} />,
      sx: {
        color: '#334155',
        background: 'linear-gradient(120deg, #FFFFFF 0%, #F1F5F9 52%, #E2E8F0 100%)',
        borderColor: '#CBD5E1',
        boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.95)',
        fontWeight: 600,
        px: 1.75,
      },
    };
  }

  if (normalizedValue === 'trilha' || normalizedValue === 'trail' || normalizedValue === 'reward') {
    return {
      label: 'Trilha',
      icon: <Iconify icon="lucide:route" sx={{ width: 14, height: 14, color: '#9A6B08' }} />,
      sx: {
        color: '#5C430B',
        background: 'linear-gradient(180deg, #FFF9E9 0%, #F8E7B7 100%)',
        borderColor: '#E0B54D',
        boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.85)',
      },
    };
  }

  if (
    normalizedValue === 'lp de eventos' ||
    normalizedValue === 'lp_events' ||
    normalizedValue === 'lp-events'
  ) {
    return {
      label: 'Viva Visitante',
      icon: <Iconify icon="lucide:building-2" sx={{ width: 16, height: 16 }} />,
      sx: {
        color: '#283C75',
        background: 'linear-gradient(120deg, #F1F5FF 0%, #DDE7FA 52%, #C7D7F3 100%)',
        borderColor: '#9FB6DE',
        boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.9)',
      },
    };
  }

  return null;
};

export default function ExhibitionOriginChip({ value, image }: ExhibitionOriginChipProps) {
  const originConfig = getOriginConfig(value);
  const originImage = image || originConfig?.image;

  if (!originConfig) return <>{value || '-'}</>;

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.75,
        width: 'fit-content',
        minWidth: 132,
        height: 28,
        px: 1,
        borderRadius: 999,
        border: '1px solid',
        fontSize: 13,
        fontWeight: 700,
        letterSpacing: 0,
        lineHeight: 1,
        whiteSpace: 'nowrap',
        ...originConfig.sx,
      }}
    >
      <Box
        component="span"
        sx={{
          position: 'relative',
          zIndex: 1,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.75,
        }}
      >
        {originImage ? (
          <Box
            component="img"
            src={originImage}
            alt=""
            sx={{ width: 52, height: 20, objectFit: 'contain', display: 'block' }}
          />
        ) : (
          <>
            {originConfig.icon}
            <Box component="span">{originConfig.label}</Box>
          </>
        )}
        {originImage && originConfig.label && <Box component="span">{originConfig.label}</Box>}
      </Box>
    </Box>
  );
}
